'use client';

/**
 * SupremeLoginForm — standalone email/password login via the SDK, mirroring
 * brief-builder's Login page. Only reached when the app is NOT embedded (in
 * embedded mode the parent supplies the token and the SDK blocks login()).
 *
 * Styling is self-contained (a scoped <style> block, no upstream component
 * deps) so this sidecar file stays rebase-safe. Source of truth lives in
 * customizations/supreme-sdk/; apply.mjs copies it into apps/web/src/supreme/.
 */

import { useState, type FormEvent } from 'react';
import { useSupremeAuth } from './SupremeAuthProvider';

// SI app for the secondary links. Adjust the paths if the SI routes differ.
const SI_BASE = 'https://app.supremegroup.ai';

const styles = `
.supreme-login-wrap {
  position: fixed; inset: 0; z-index: 2147483646;
  display: flex; align-items: center; justify-content: center; padding: 24px;
  background: #ffffff;
  font: 14px/1.5 ui-sans-serif, system-ui, -apple-system, sans-serif;
  color: #111827;
}
.supreme-login-card {
  width: 100%; max-width: 400px;
  background: #ffffff; border: 1px solid #e5e7eb; border-radius: 16px;
  padding: 32px; box-shadow: 0 10px 30px rgba(17, 24, 39, 0.08);
}
.supreme-login-title { font-size: 24px; font-weight: 700; color: #374151; text-align: center; margin: 0 0 6px; }
.supreme-login-subtitle { font-size: 14px; color: #6b7280; text-align: center; margin: 0 0 28px; }
.supreme-login-labelrow { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 6px; }
.supreme-login-label { font-size: 13px; font-weight: 600; color: #111827; }
.supreme-login-link { font-size: 13px; color: #6366f1; text-decoration: none; }
.supreme-login-link:hover { text-decoration: underline; }
.supreme-login-input {
  width: 100%; box-sizing: border-box; padding: 10px 13px; margin-bottom: 18px;
  font: inherit; font-size: 14px; color: #111827;
  background: #ffffff; border: 1px solid #d1d5db; border-radius: 8px;
  outline: none; transition: border-color 0.15s, box-shadow 0.15s;
}
.supreme-login-input::placeholder { color: #9ca3af; }
.supreme-login-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.18); }
.supreme-login-error { font-size: 13px; color: #dc2626; margin: 0 0 16px; }
.supreme-login-btn {
  width: 100%; padding: 11px 14px; font: inherit; font-size: 14px; font-weight: 600;
  color: #ffffff; background: #6366f1; border: 0; border-radius: 8px; cursor: pointer;
  transition: background-color 0.15s;
}
/* Force the indigo on every interactive state so the app's global button
   styles can't turn it white on hover/focus. !important overrides those. */
.supreme-login-btn:hover,
.supreme-login-btn:focus,
.supreme-login-btn:focus-visible,
.supreme-login-btn:active {
  background-color: #4f46e5 !important;
  color: #ffffff !important;
}
.supreme-login-btn:disabled { opacity: 0.65; cursor: default; }
.supreme-login-foot { text-align: center; font-size: 13px; color: #6b7280; margin: 20px 0 0; }
`;

export function SupremeLoginForm() {
  const { login } = useSupremeAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (!result.success) {
        setError(result.error || 'Sign in failed. Check your credentials.');
      }
      // On success the SDK updates auth state; the gate swaps to the app.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="supreme-login-wrap">
      <style>{styles}</style>
      <form className="supreme-login-card" onSubmit={handleSubmit}>
        <h1 className="supreme-login-title">Sign in</h1>
        <p className="supreme-login-subtitle">Sign in to access your account</p>

        <div className="supreme-login-labelrow">
          <label className="supreme-login-label" htmlFor="supreme-email">Email Address</label>
        </div>
        <input
          id="supreme-email"
          className="supreme-login-input"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
        />

        <div className="supreme-login-labelrow">
          <label className="supreme-login-label" htmlFor="supreme-password">Password</label>
          <a className="supreme-login-link" href={`${SI_BASE}/forgot-password`} target="_blank" rel="noreferrer">
            Forgot password?
          </a>
        </div>
        <input
          id="supreme-password"
          className="supreme-login-input"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Your Password"
        />

        {error && <p className="supreme-login-error" role="alert">{error}</p>}

        <button className="supreme-login-btn" type="submit" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="supreme-login-foot">
          Don&apos;t have an account yet?{' '}
          <a className="supreme-login-link" href={`${SI_BASE}/register`} target="_blank" rel="noreferrer">
            Sign up
          </a>
          .
        </p>
      </form>
    </div>
  );
}
